import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/stores/authStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useServices, type Service } from '@/hooks/useServices';
import {
  indexMatrixCells,
  matrixCellKey,
  useSkillMatrix,
  useUpsertSkillCell,
  type SkillMatrixCell,
  type SkillMatrixSkill,
  type SkillMatrixUser,
  type UpsertSkillCellBody,
} from '@/hooks/useSkillMatrix';
import {
  SKILL_CATEGORY_OPTIONS,
  SKILL_LEVEL_OPTIONS,
  categoryLabelKey,
  type CanonicalLevel,
} from '@/utils/skillLevels';
import { isQualifiedFor } from '@/utils/skill-matching';
import { MatrixCell } from './MatrixCell';
import { CertificateDialog } from './CertificateDialog';

interface CompetenceMatrixProps {
  filterUserId?: string | null;
  onClearUserFilter?: () => void;
}

function roleLabelKey(role: string): string {
  if (role === 'reception') return 'roles.receptionist';
  if (role === 'admin') return 'roles.admin';
  return `roles.${role}`;
}

export function CompetenceMatrix({ filterUserId, onClearUserFilter }: CompetenceMatrixProps) {
  const { t } = useTranslation('hr');
  const { t: ts } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const isMobile = useIsMobile();
  const canManage = useAuthStore((s) => s.hasPermission('hr.manage_team'));
  const { data, isLoading, isError, refetch } = useSkillMatrix();
  const upsert = useUpsertSkillCell();
  const servicesQuery = useServices({ isActive: true });

  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [activeUserId, setActiveUserId] = useState<string | null>(null);
  const [activeSkillId, setActiveSkillId] = useState<string | null>(null);
  const [certificateOpen, setCertificateOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [bulkUser, setBulkUser] = useState<SkillMatrixUser | null>(null);
  const [bulkCategory, setBulkCategory] = useState<string>('');
  const [bulkLevel, setBulkLevel] = useState<CanonicalLevel>('guided');

  const cellMap = useMemo(() => indexMatrixCells(data?.cells ?? []), [data?.cells]);

  const handleOpen = useCallback((userId: string, skillId: string, el: HTMLElement) => {
    setActiveUserId(userId);
    setActiveSkillId(skillId);
    setAnchor(el);
  }, []);

  const applyCell = useCallback(
    (userId: string, skillId: string, body: UpsertSkillCellBody, remove = false) => {
      const existing = cellMap.get(matrixCellKey(userId, skillId));
      void upsert.mutateAsync({
        userId,
        skillId,
        body,
        ...(existing?.updatedAt ? { updatedAt: existing.updatedAt } : {}),
        ...(remove ? { remove: true } : {}),
      });
    },
    [cellMap, upsert],
  );

  const handleSetLevel = useCallback(
    (userId: string, skillId: string, level: CanonicalLevel) => {
      const existing = cellMap.get(matrixCellKey(userId, skillId));
      applyCell(userId, skillId, {
        level,
        wantsToLearn: existing?.wantsToLearn ?? false,
      });
    },
    [applyCell, cellMap],
  );

  const requiredSkillIds = useMemo(() => {
    if (!serviceId) return new Set<string>();
    const service = (servicesQuery.data ?? []).find((item) => item.id === serviceId);
    return new Set((service?.skills ?? []).map((link) => link.skillId));
  }, [serviceId, servicesQuery.data]);

  const requiredLinks = useMemo(() => {
    if (!serviceId) return [];
    const service = (servicesQuery.data ?? []).find((item) => item.id === serviceId);
    return service?.skills ?? [];
  }, [serviceId, servicesQuery.data]);

  const skillById = useMemo(() => {
    const map = new Map<string, SkillMatrixSkill>();
    for (const category of data?.categories ?? []) {
      for (const skill of category.skills) map.set(skill.id, skill);
    }
    return map;
  }, [data?.categories]);

  const lackingUserIds = useMemo(() => {
    if (requiredLinks.length === 0) return new Set<string>();
    const lacking = new Set<string>();
    for (const user of data?.users ?? []) {
      const held = requiredLinks.map((link) => {
        const cell = cellMap.get(matrixCellKey(user.id, link.skillId));
        const skill = skillById.get(link.skillId);
        return {
          skillId: link.skillId,
          level: cell?.level ?? null,
          wantsToLearn: cell?.wantsToLearn ?? false,
          expiresAt: cell?.certificate.expiresAt ?? null,
          requiresValidCertificate: skill?.requiresValidCertificate ?? false,
        };
      });
      const ok = requiredLinks.every((link) => {
        const row = held.find((item) => item.skillId === link.skillId);
        if (!row) return false;
        return isQualifiedFor(
          { level: row.level, wantsToLearn: row.wantsToLearn, expiresAt: row.expiresAt },
          {
            minimumProficiency: link.minimumProficiency,
            requiresValidCertificate: row.requiresValidCertificate,
          },
        );
      });
      if (!ok) lacking.add(user.id);
    }
    return lacking;
  }, [cellMap, data?.users, requiredLinks, skillById]);

  const visibleCategories = useMemo(() => {
    const categories = (data?.categories ?? []).filter((category) => category.skills.length > 0);
    if (selectedCategories.length === 0) return categories;
    return categories.filter((category) => selectedCategories.includes(category.key));
  }, [data?.categories, selectedCategories]);

  const query = search.trim().toLowerCase();

  const visibleSkills = useMemo(() => {
    const skills = visibleCategories.flatMap((category) =>
      category.skills.map((skill) => ({ ...skill, categoryKey: category.key })),
    );
    if (!query) return skills;
    const skillHits = skills.filter((skill) => skill.name.toLowerCase().includes(query));
    return skillHits.length > 0 ? skillHits : skills;
  }, [query, visibleCategories]);

  const visibleUsers = useMemo(() => {
    let users = (data?.users ?? []).filter((user) => user.isActive);
    if (filterUserId) users = users.filter((user) => user.id === filterUserId);
    if (roleFilter !== 'all') users = users.filter((user) => user.role === roleFilter);
    if (query) {
      const userHits = users.filter((user) => user.name.toLowerCase().includes(query));
      const skillHits = visibleCategories
        .flatMap((category) => category.skills)
        .some((skill) => skill.name.toLowerCase().includes(query));
      if (userHits.length > 0 && !skillHits) users = userHits;
      else if (userHits.length > 0 && skillHits) users = userHits;
    }
    if (expiringOnly) {
      users = users.filter((user) =>
        visibleSkills.some((skill) => {
          const cell = cellMap.get(matrixCellKey(user.id, skill.id));
          return cell?.certificate.status === 'expiring';
        }),
      );
    }
    return users;
  }, [
    cellMap,
    data?.users,
    expiringOnly,
    filterUserId,
    query,
    roleFilter,
    visibleCategories,
    visibleSkills,
  ]);

  const roles = useMemo(() => {
    const set = new Set((data?.users ?? []).map((user) => user.role));
    return [...set].sort();
  }, [data?.users]);

  const groupedVisible = useMemo(() => {
    return visibleCategories
      .map((category) => ({
        ...category,
        skills: category.skills.filter((skill) =>
          visibleSkills.some((visible) => visible.id === skill.id),
        ),
      }))
      .filter((category) => category.skills.length > 0);
  }, [visibleCategories, visibleSkills]);

  const activeCell: SkillMatrixCell | undefined =
    activeUserId && activeSkillId
      ? cellMap.get(matrixCellKey(activeUserId, activeSkillId))
      : undefined;
  const activeSkill = activeSkillId ? skillById.get(activeSkillId) : undefined;

  const closePopover = () => {
    setAnchor(null);
  };

  const handleBulkApply = async () => {
    if (!bulkUser || !bulkCategory) return;
    const category = (data?.categories ?? []).find((item) => item.key === bulkCategory);
    if (!category) return;
    for (const skill of category.skills) {
      const existing = cellMap.get(matrixCellKey(bulkUser.id, skill.id));
      await upsert.mutateAsync({
        userId: bulkUser.id,
        skillId: skill.id,
        body: { level: bulkLevel, wantsToLearn: existing?.wantsToLearn ?? false },
        ...(existing?.updatedAt ? { updatedAt: existing.updatedAt } : {}),
      });
    }
    setBulkUser(null);
  };

  if (isLoading) {
    return (
      <Stack spacing={1}>
        <Skeleton variant="rectangular" height={48} />
        <Skeleton variant="rectangular" height={240} />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void refetch()}>
            {tc('errors.refresh')}
          </Button>
        }
      >
        {t('skillsMatrix.loadFailed')}
      </Alert>
    );
  }

  const popoverLevel = activeCell?.level ?? '';
  const filteredUser = filterUserId
    ? (data?.users ?? []).find((user) => user.id === filterUserId)
    : undefined;

  return (
    <Stack spacing={2} data-testid="competence-matrix">
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="flex-end">
        <TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('skillsMatrix.searchPlaceholder')}
          sx={{ minWidth: 220 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="skills-matrix-role-filter">{t('skillsMatrix.roleFilter')}</InputLabel>
          <Select
            labelId="skills-matrix-role-filter"
            id="skills-matrix-role-filter-select"
            value={roleFilter}
            label={t('skillsMatrix.roleFilter')}
            onChange={(event) => setRoleFilter(event.target.value)}
          >
            <MenuItem value="all">{t('skillsMatrix.allRoles')}</MenuItem>
            {roles.map((role) => (
              <MenuItem key={role} value={role}>
                {ts(roleLabelKey(role))}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          size="small"
          sx={{ minWidth: 220 }}
          options={servicesQuery.data ?? []}
          getOptionLabel={(option: Service) => option.name}
          value={(servicesQuery.data ?? []).find((item) => item.id === serviceId) ?? null}
          onChange={(_event, value) => setServiceId(value?.id ?? null)}
          renderInput={(params) => (
            <TextField {...params} label={t('skillsMatrix.servicePicker')} />
          )}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={expiringOnly}
              onChange={(event) => setExpiringOnly(event.target.checked)}
              size="small"
            />
          }
          label={t('skillsMatrix.expiringOnly')}
        />
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {SKILL_CATEGORY_OPTIONS.map((option) => {
          const selected =
            selectedCategories.length === 0 || selectedCategories.includes(option.value);
          return (
            <Chip
              key={option.value}
              data-testid={`category-chip-${option.value}`}
              label={ts(option.labelKey)}
              clickable
              color={selected && selectedCategories.length > 0 ? 'primary' : 'default'}
              variant={selected && selectedCategories.length > 0 ? 'filled' : 'outlined'}
              onClick={() => {
                setSelectedCategories((current) =>
                  current.includes(option.value)
                    ? current.filter((key) => key !== option.value)
                    : [...current, option.value],
                );
              }}
            />
          );
        })}
      </Stack>

      {filteredUser && (
        <Chip
          label={t('skillsMatrix.filteredToUser', { name: filteredUser.name })}
          {...(onClearUserFilter ? { onDelete: onClearUserFilter } : {})}
        />
      )}

      {isMobile ? (
        <Stack spacing={1} data-testid="skills-matrix-accordion">
          {visibleUsers.map((user) => (
            <Accordion key={user.id} defaultExpanded={user.id === filterUserId}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontWeight={600}>{user.name}</Typography>
                  <Chip size="small" label={ts(roleLabelKey(user.role))} />
                  {lackingUserIds.has(user.id) && (
                    <Chip size="small" color="warning" label={t('skillsMatrix.lackingRequired')} />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {canManage && (
                  <Button
                    size="small"
                    sx={{ mb: 1 }}
                    onClick={() => {
                      setBulkUser(user);
                      setBulkCategory(groupedVisible[0]?.key ?? '');
                    }}
                  >
                    {t('skillsMatrix.setForGroup')}
                  </Button>
                )}
                <Stack spacing={1}>
                  {visibleSkills.map((skill) => {
                    const cell = cellMap.get(matrixCellKey(user.id, skill.id));
                    return (
                      <Box key={skill.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {skill.name}
                        </Typography>
                        <Box sx={{ minWidth: 140 }}>
                          <MatrixCell
                            userId={user.id}
                            skillId={skill.id}
                            userName={user.name}
                            skillName={skill.name}
                            level={cell?.level ?? null}
                            wantsToLearn={cell?.wantsToLearn ?? false}
                            certificateStatus={cell?.certificate.status}
                            requiresValidCertificate={skill.requiresValidCertificate}
                            canEdit={canManage}
                            highlighted={requiredSkillIds.has(skill.id)}
                            lacking={lackingUserIds.has(user.id) && requiredSkillIds.has(skill.id)}
                            onOpen={handleOpen}
                            onSetLevel={handleSetLevel}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      ) : (
        <TableContainer
          sx={{ maxHeight: '70vh', overflow: 'auto' }}
          data-testid="skills-matrix-table"
        >
          <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    top: 0,
                    zIndex: 5,
                    bgcolor: 'background.paper',
                    minWidth: 180,
                  }}
                />
                {groupedVisible.map((category) => (
                  <TableCell
                    key={category.key}
                    align="center"
                    colSpan={category.skills.length}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 4,
                      bgcolor: 'background.paper',
                      borderLeft: 1,
                      borderColor: 'divider',
                    }}
                  >
                    {ts(categoryLabelKey(category.key))}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    top: 40,
                    zIndex: 5,
                    bgcolor: 'background.paper',
                    fontWeight: 600,
                  }}
                >
                  {t('skillsMatrix.staff')}
                </TableCell>
                {visibleSkills.map((skill) => (
                  <TableCell
                    key={skill.id}
                    align="center"
                    sx={{
                      position: 'sticky',
                      top: 40,
                      zIndex: 3,
                      bgcolor: requiredSkillIds.has(skill.id)
                        ? 'action.selected'
                        : 'background.paper',
                      maxWidth: 96,
                    }}
                  >
                    <Tooltip title={skill.name}>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ display: 'block', maxWidth: 88 }}
                      >
                        {skill.name}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      bgcolor: lackingUserIds.has(user.id) ? 'warning.light' : 'background.paper',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ts(roleLabelKey(user.role))}
                        </Typography>
                      </Box>
                      {lackingUserIds.has(user.id) && (
                        <Chip size="small" color="warning" label={t('skillsMatrix.lackingRequired')} />
                      )}
                      {canManage && (
                        <Button
                          size="small"
                          onClick={() => {
                            setBulkUser(user);
                            setBulkCategory(groupedVisible[0]?.key ?? '');
                          }}
                        >
                          {t('skillsMatrix.setForGroup')}
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                  {visibleSkills.map((skill) => {
                    const cell = cellMap.get(matrixCellKey(user.id, skill.id));
                    return (
                      <TableCell key={skill.id} align="center" sx={{ p: 0.5 }}>
                        <MatrixCell
                          userId={user.id}
                          skillId={skill.id}
                          userName={user.name}
                          skillName={skill.name}
                          level={cell?.level ?? null}
                          wantsToLearn={cell?.wantsToLearn ?? false}
                          certificateStatus={cell?.certificate.status}
                          requiresValidCertificate={skill.requiresValidCertificate}
                          canEdit={canManage}
                          highlighted={requiredSkillIds.has(skill.id)}
                          lacking={lackingUserIds.has(user.id) && requiredSkillIds.has(skill.id)}
                          onOpen={handleOpen}
                          onSetLevel={handleSetLevel}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 240 }} data-testid="skill-cell-popover">
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {activeSkill?.name}
          </Typography>
          <RadioGroup
            value={popoverLevel}
            onChange={(event) => {
              if (!activeUserId || !activeSkillId) return;
              const value = event.target.value;
              if (value === '') {
                if (activeCell?.wantsToLearn) {
                  applyCell(activeUserId, activeSkillId, { level: null, wantsToLearn: true });
                } else {
                  applyCell(activeUserId, activeSkillId, {}, true);
                  closePopover();
                }
                return;
              }
              applyCell(activeUserId, activeSkillId, {
                level: value as CanonicalLevel,
                wantsToLearn: activeCell?.wantsToLearn ?? false,
              });
            }}
          >
            <FormControlLabel value="" control={<Radio />} label={ts('skills.levels.none')} />
            {SKILL_LEVEL_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={ts(option.labelKey)}
              />
            ))}
          </RadioGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={activeCell?.wantsToLearn ?? false}
                onChange={(event) => {
                  if (!activeUserId || !activeSkillId) return;
                  const wants = event.target.checked;
                  if (!wants && !activeCell?.level) {
                    applyCell(activeUserId, activeSkillId, {}, true);
                    return;
                  }
                  applyCell(activeUserId, activeSkillId, {
                    level: activeCell?.level ?? null,
                    wantsToLearn: wants,
                  });
                }}
              />
            }
            label={ts('skills.levels.wantsToLearn')}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              size="small"
              onClick={() => {
                setCertificateOpen(true);
                closePopover();
              }}
            >
              {t('skillsMatrix.certificateAction')}
            </Button>
            <Button
              size="small"
              color="error"
              onClick={() => setRemoveOpen(true)}
            >
              {t('skillsMatrix.removeAssignment')}
            </Button>
          </Stack>
        </Box>
      </Popover>

      <ConfirmDialog
        open={removeOpen}
        title={t('skillsMatrix.removeAssignment')}
        message={t('skillsMatrix.removeConfirm', { skill: activeSkill?.name ?? '' })}
        confirmText={tc('delete')}
        confirmColor="error"
        onConfirm={() => {
          if (activeUserId && activeSkillId) {
            applyCell(activeUserId, activeSkillId, {}, true);
          }
          setRemoveOpen(false);
          closePopover();
        }}
        onCancel={() => setRemoveOpen(false)}
      />

      {activeUserId && activeSkillId && (
        <CertificateDialog
          open={certificateOpen}
          userId={activeUserId}
          skillId={activeSkillId}
          skillName={activeSkill?.name ?? ''}
          cell={activeCell}
          onClose={() => setCertificateOpen(false)}
        />
      )}

      <ResponsiveDialog
        open={Boolean(bulkUser)}
        onClose={() => setBulkUser(null)}
        maxWidth="xs"
        fullWidth
        mobileTitle={t('skillsMatrix.setForGroup')}
      >
        <DialogTitle>{t('skillsMatrix.setForGroupTitle', { name: bulkUser?.name ?? '' })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('skillsMatrix.bulkCategory')}</InputLabel>
              <Select
                value={bulkCategory}
                label={t('skillsMatrix.bulkCategory')}
                onChange={(event) => setBulkCategory(event.target.value)}
              >
                {(data?.categories ?? [])
                  .filter((category) => category.skills.length > 0)
                  .map((category) => (
                    <MenuItem key={category.key} value={category.key}>
                      {ts(categoryLabelKey(category.key))}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <RadioGroup
              value={bulkLevel}
              onChange={(event) => setBulkLevel(event.target.value as CanonicalLevel)}
            >
              {SKILL_LEVEL_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={ts(option.labelKey)}
                />
              ))}
            </RadioGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUser(null)}>{tc('cancel')}</Button>
          <Button variant="contained" onClick={() => void handleBulkApply()}>
            {tc('save')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </Stack>
  );
}
